// ── CUOTAS DE UN EVENTO ────────────────────────────────────────
// El carrusel de cuotas, sus botones y el diccionario de mercados vivían
// dentro de App.jsx, así que la agencia no podía usarlos: App.jsx no
// exportaba ni el componente ni el CSS. Lo que había en Agencia.jsx era
// una copia a mano, y una copia se separa del original en el rediseño
// siguiente. Acá viven una sola vez y las dos pantallas los importan.
//
// FORMA OBLIGATORIA: todo se declara como `function Nombre(` a secas y se
// exporta al final con un solo `export {}`. Media docena de pruebas de
// este repo recortan el cuerpo de un componente leyendo el texto crudo
// del archivo y cortando en el próximo `\nfunction `; un
// `export function` es invisible para ese corte y el cuerpo quedaría
// pegado al del componente anterior.
import { useState, useRef } from "react";
import { oscuro as Q, F_NUM, F_BODY, inkOn, RADII, SPACING } from "./theme";
// El diccionario de mercados y el traductor de selecciones se mudaron a
// `etiquetasDeMercado.js`, que los tiene en los tres idiomas. Se importan con
// el nombre de antes a propósito: `Admin.jsx` y `Agencia.jsx` los importan DE
// ACÁ, y la superficie pública de este archivo no cambia. Sin el cuarto
// argumento los tres siguen contestando en español, que es exactamente lo que
// dibujaban hasta hoy.
import { tituloMercado, ordenMercado,
  etiquetaDeSeleccion as etiquetaResultado } from "./etiquetasDeMercado";

const fmt = n => Number(n||0).toFixed(2);


// ── ETIQUETA CORTA DE UN EQUIPO ───────────────────────────────
// El botón de cuota es angosto: en un teléfono de 390px entran tres,
// así que el nombre del equipo tiene que achicarse. Antes se achicaba
// con `nombre.split(" ")[0]` —la primera palabra— y eso dejaba
// "Union Minas vs Union Comercio" dibujado como "Union | X | Union":
// el jugador no podía saber a cuál le estaba apostando. En un producto
// de plata eso no es cosmético, es una apuesta a ciegas.
//
// La regla: la etiqueta arranca en la palabra donde los dos nombres
// dejan de parecerse, no en la primera. Lo que comparten se tira,
// porque es justo lo que NO distingue; lo que los separa va adelante y
// entero. "Union Minas vs Union Comercio" se dibuja "Minas | X |
// Comercio". El nombre completo igual está arriba, en el título de la
// tarjeta: el botón no tiene que identificar al equipo, tiene que
// distinguirlo del otro, y tiene que lograrlo sin recortarse.
//
// Arrancar por la diferencia no es un gusto: el rótulo se recorta con
// puntos suspensivos cuando no entra, así que si los dos empezaran
// igual ("N. Y. Ran…" / "N. Y. Isla…") el recorte se come justo la
// parte que distingue. Con la diferencia adelante, el recorte nunca
// puede volver a hacer gemelos a dos equipos.

// Medido en el navegador a 390px: el botón del medio mide 80px y la
// caja del rótulo 70px, que a 12px de fuente son unos 10 caracteres.
// Arriba de eso el nombre compartido no entra y conviene tirarlo.
const MAX_ETIQUETA = 10;

const sinTildes = s =>
  String(s||"").normalize("NFD").replace(/[̀-ͯ]/g,"").toLowerCase();

// Un nombre del catálogo trae a veces un calificativo pegado al final
// que dice de qué plantel se trata: "(f)" femenino, "(res.)" reservas,
// "Sub-21" juveniles. No es parte del nombre y se trata aparte, porque
// a veces es justo lo único que separa a los dos equipos.
const RE_CALIFICATIVO = /\s*(\([^()]*\)|sub[-\s]?\d{1,2}|u\d{2})\s*$/i;

function partirEquipo(nombre){
  let base = String(nombre||"").trim();
  const calificativos = [];
  for(;;){
    const m = base.match(RE_CALIFICATIVO);
    if(!m) break;
    const resto = base.replace(RE_CALIFICATIVO,"").trim();
    if(!resto) break;           // el nombre es solo el calificativo
    calificativos.unshift(m[1].trim());
    base = resto;
  }
  return { base, calificativo: calificativos.join(" ") };
}

function etiquetasDeEquipos(home, away){
  const H = partirEquipo(home), A = partirEquipo(away);
  const palH = H.base.split(/\s+/).filter(Boolean);
  const palA = A.base.split(/\s+/).filter(Boolean);

  // Primera palabra en la que los dos nombres dejan de coincidir. Se
  // comparan sin tildes ni mayúsculas: "Unión" y "Union" se leen igual
  // en un botón, así que tampoco alcanzan para distinguir.
  const basesIguales = sinTildes(H.base)===sinTildes(A.base);
  let corte = 0;
  if(!basesIguales){
    while(corte<palH.length && corte<palA.length
          && sinTildes(palH[corte])===sinTildes(palA[corte])) corte++;
  }
  const cortePara = pal => Math.min(corte, Math.max(pal.length-1, 0));
  const nH = cortePara(palH), nA = cortePara(palA);

  const entero = (pal,n) => pal.slice(0,n+1).join(" ");
  const soloLaDiferencia = (pal,n) => pal[n]||"";

  // El nombre compartido se conserva solo cuando entra entero en el
  // botón ("CBPS Azul" / "CBPS R"). Si no entra, se va: vale más
  // "Rangers" / "Islanders" completos que "N. Y. Ran…" / "N. Y. Isla…"
  // recortados. Las dos etiquetas usan la misma forma para que se lean
  // como un par y no como dos criterios distintos.
  const entran = entero(palH,nH).length<=MAX_ETIQUETA
              && entero(palA,nA).length<=MAX_ETIQUETA;

  // El calificativo solo ocupa ancho cuando es lo que distingue: si los
  // dos equipos son "(f)", mostrarlo dos veces no aporta nada.
  const mostrarCalif = sinTildes(H.calificativo)!==sinTildes(A.calificativo);

  const armar = (parte,pal,n) => {
    const nombre = pal.length
      ? (entran?entero(pal,n):soloLaDiferencia(pal,n))
      : parte.base;
    const cola = mostrarCalif && parte.calificativo ? ` ${parte.calificativo}` : "";
    return `${nombre}${cola}`.trim();
  };

  let eH = armar(H,palH,nH) || String(home||"").trim();
  let eA = armar(A,palA,nA) || String(away||"").trim();

  // Último recurso: el catálogo mandó el mismo nombre dos veces. Antes
  // que dos botones gemelos, local y visitante.
  if(eH && sinTildes(eH)===sinTildes(eA)){ eH=`${eH} (L)`; eA=`${eA} (V)`; }

  return { home:eH, away:eA };
}

// ── ODDS ───────────────────────────────────────────────────────
//
// `soloLectura` dibuja la MISMA fila sin la venta. Nació para el panel de
// eventos del admin, que es la única pantalla que mira estas cuotas sin
// poder apostarlas: ahí los precios están para decidir si se bloquea o se
// limita un mercado, y un botón sería una invitación a armar un boleto
// desde la administración.
//
// Por qué un modo acá y no una fila propia en `Admin.jsx`: lo que homologa
// la tarjeta del admin con la del jugador y la de la agencia no es el
// parecido, son las piezas —`etiquetasDeEquipos`, el fondo hundido, el
// número en oro, el repliegue a `ev.odds` y el "No disponible"—. Escritas
// de nuevo del otro lado serían una copia, y una copia se separa del
// original en el próximo rediseño: es exactamente lo que ya pasó con el
// carrusel duplicado a mano entre el jugador y la agencia (ver el
// encabezado de este archivo y `unSoloCarrusel.test.js`).
//
// Lo que cambia es el ELEMENTO, no el estilo: un `div` no se puede apretar,
// no toma foco con el tabulador y no tiene a dónde mandar un `onToggle`,
// así que no hay camino por el que una selección entre desde el panel. Y el
// estilo no se bifurca —misma celda, mismo token— salvo en lo que delata un
// control que no existe: el cursor y la transición de apretado.
function OddsButtons({ ev, market, bets=[], onToggle, live=false,
    soloLectura=false }){
  const markets = ev.markets || {};
  const mkt = markets[market] || {};
  const color = live?Q.pink:Q.violet;
  const home = ev.h||ev.home||"";
  const away = ev.a||ev.away||"";

  // `label` queda con el nombre completo: es lo que viaja al carrito y
  // al boleto. Lo que se achica es solo lo que se dibuja en el botón.
  const corto = etiquetasDeEquipos(home, away);

  // `nombre` es el NOMBRE DEL EMISOR: la clave literal de
  // `markets[market]`, que es exactamente lo que `claves_de_seleccion`
  // indexa del lado de GR8. Viaja AL LADO de `label`, nunca en su lugar:
  // `label` es la identidad del pick en el carrito (`b.label===o.label`,
  // abajo) y además lo que el jugador lee. Sin `nombre`, el pick pasa la
  // venta —el `sport_key` trae el prefijo `gr8_`— y falla al liquidar con
  // `nombre_no_encontrado`, así que lo paga una persona a mano.
  //
  // `nombre:""` cuando la cuota NO salió del diccionario: el repliegue
  // viejo la lee de `ev.odds`, donde no hay clave de emisor que mandar.
  // Inventar "Draw" ahí sería afirmar que GR8 publicó ese resultado en
  // ese mercado; sin clave el pick sigue sin liquidar, pero no miente.
  //
  // Y `label` sale de `etiquetaResultado`, el MISMO traductor que usan el
  // carrusel y "Todos los mercados". Antes acá había un segundo juego de
  // literales escritos a mano, y ya se habían separado: este archivo daba
  // `Más 2.5` y el otro camino `Más de 2.5`, o sea el mismo total entrando
  // DOS VECES al carrito y ninguno marcado desde la otra vista.
  const outcomes = [];
  const agregar = (nombre, val, short) => {
    if(!val) return;
    outcomes.push({
      label: etiquetaResultado(nombre, home, away),
      nombre: nombre in mkt ? nombre : "",
      short, val,
    });
  };

  if(market==="h2h"){
    agregar(home,   mkt[home]||ev.odds?.L, corto.home);
    agregar("Draw", mkt["Draw"]||ev.odds?.E, "X");
    agregar(away,   mkt[away]||ev.odds?.V, corto.away);
  } else if(market==="totals"){
    const keys = Object.keys(mkt);
    const overKey = keys.find(k=>k.startsWith("Over"));
    const underKey = keys.find(k=>k.startsWith("Under"));
    const line = overKey ? overKey.replace("Over ","").replace("Over","") : "2.5";
    if(overKey) agregar(overKey, mkt[overKey], `+${line}`);
    if(underKey) agregar(underKey, mkt[underKey], `-${line}`);
  } else if(market==="btts"){
    agregar("Yes", mkt["Yes"], "Sí");
    agregar("No",  mkt["No"],  "No");
  } else if(market==="spreads"){
    Object.entries(mkt).slice(0,3).forEach(([k,v])=>{
      agregar(k, v, k.split(" ").slice(-1)[0]);
    });
  }

  if(!outcomes.length) return(
    <div style={{textAlign:"center",color:Q.dim,fontSize:12,padding:"8px 0",
      fontFamily:F_BODY}}>No disponible</div>
  );

  return(
    <div style={{display:"flex",gap:SPACING[4]}}>
      {outcomes.map((o,i)=>{
        // En modo informativo no hay carrito que consultar: nada puede
        // estar elegido, así que `bets` no se mira siquiera.
        const sel=!soloLectura&&bets.some(b=>b.id===ev.id&&b.label===o.label);
        const Celda=soloLectura?"div":"button";
        return(
          // Sin borde: el botón ya se recorta contra la tarjeta por su
          // fondo hundido (`inset`), y cuando está elegido se pinta
          // entero del color del acento. El hilo gris no agregaba un
          // dato, agregaba una línea —tres por partido, en una lista de
          // doscientos.
          <Celda key={i} title={o.label}
            onClick={soloLectura?undefined
              :e=>{ onToggle(ev,o.label,o.val,market,o.nombre);
                despejarCarrito(e.currentTarget); }}
            style={{
            flex:"1 1 0",minWidth:0,overflow:"hidden",boxSizing:"border-box",
            background:sel?color:Q.inset,
            border:"none",
            borderRadius:RADII.md,padding:"8px 4px",
            cursor:soloLectura?"default":"pointer",textAlign:"center",
            transition:soloLectura?"none":"background 0.13s",
          }}>
            <div style={{color:sel?inkOn(color):Q.dim,fontSize:12,fontFamily:F_BODY,
              fontWeight:600,letterSpacing:0.6,
              marginBottom:1,lineHeight:1.1,overflow:"hidden",textOverflow:"ellipsis",
              whiteSpace:"nowrap",maxWidth:"100%"}}>{o.short||o.label}</div>
            <div style={{color:sel?inkOn(color):Q.gold,fontWeight:700,fontSize:17,
              lineHeight:1.05,fontFamily:F_NUM}}>{fmt(o.val)}</div>
            {/* El estado en vivo lo dice la tarjeta una vez, arriba, en
                español. Acá colgaba un "● LIVE" debajo de CADA cuota:
                tres veces por partido, en inglés, repitiendo lo que el
                borde teñido y la chapa ya dicen, y robándole alto a lo
                único que el jugador vino a leer. Queda el color, que es
                la parte que distingue sin ocupar lugar. */}
          </Celda>
        );
      })}
    </div>
  );
}

// ── CUOTAS EN CARRUSEL ─────────────────────────────────────────
// La tarjeta mostraba un solo mercado —el 1X2— y todo lo demás vivía
// detrás de "▼ Todos los mercados". Para comparar un Más/Menos había que
// abrir el partido, cerrarlo y abrir el siguiente. Ahora los primeros
// mercados entran en una pista que se corre con el pulgar.
//
// CUÁNTOS ENTRAN: `CARRUSEL_MAX` páginas, y solo de los mercados cuyos
// resultados caben en una fila del ancho de la tarjeta (2 o 3). No es un
// número elegido de arriba:
//
//  · El catálogo trae 20 clases de mercado por partido en el peor caso.
//    De las 20, las que tienen 2 o 3 resultados se dibujan con la misma
//    fila que el jugador ya sabe tocar; las de 4 en adelante —resultado
//    exacto (hasta 67 casillas), líneas alternativas (hasta 32), goles
//    por equipo— necesitan la grilla de dos columnas con scroll propio
//    que el panel expandido ya tiene. Meterlas en una fila de tres sería
//    recortarles los nombres hasta que no se lean.
//  · Medido sobre el catálogo real de GR8 (199 partidos, 13 deportes):
//    146 partidos tienen 3 páginas o menos y 173 tienen 5 o menos. El
//    tope de 5 recorta la cola —26 partidos—, no el caso normal.
//  · Pasando los seis, los puntitos dejan de contarse de un vistazo y
//    el carrusel empieza a pedir lo mismo que venimos a evitar: un
//    mapa de lo que hay.
//
// Lo que no entra NO se pierde: sigue entero en "Todos los mercados",
// que queda abajo como estaba. El carrusel es el atajo, no el reemplazo.
//
// POR QUÉ CADA PÁGINA LLEVA SU TÍTULO ADENTRO DE LA PISTA, y no un
// rótulo fijo arriba: el título viaja con sus cuotas, así que durante el
// desliz nunca se lee el nombre de un mercado encima de los números de
// otro, y al asomar la página siguiente ya se ve a qué se está yendo.
// Los puntitos quedan para lo único que los puntitos saben decir:
// cuántas páginas hay y en cuál estoy.
const CARRUSEL_MAX = 5;

// Dos o tres resultados: lo que entra en una fila sin recortar nombres.
//
// Las dos ayudantes de acá abajo se declaran como `function` a secas y se
// exportan al final, en vez de `export function`: media docena de pruebas
// de este repo recortan el cuerpo de un componente buscando el próximo
// `\nfunction `, y un `export function` es invisible para ese corte —
// quedaría pegado al cuerpo del componente anterior.
function mercadosDelCarrusel(ev){
  const todos = (ev && ev.markets) || {};
  return Object.keys(todos)
    .filter(k=>{
      const n = Object.keys(todos[k]||{}).length;
      return n>=2 && n<=3;
    })
    .sort((a,b)=>ordenMercado(a)-ordenMercado(b))
    .slice(0, CARRUSEL_MAX);
}

// La etiqueta que viaja al carrito es la larga, `etiquetaResultado` — la
// misma que manda el panel expandido. Es la condición para que un mismo
// resultado tomado desde el carrusel y desde "Todos los mercados" sea UNA
// selección y no dos con nombres distintos. Lo corto es solo lo que se
// dibuja adentro del botón.
//
// Esto era verdad para dos de los tres caminos y mentira para el tercero:
// `OddsButtons` escribía sus etiquetas largas a mano y había quedado en
// `Más 2.5` / `Menos 2.5` / `Ambos anotan`, contra `Más de 2.5` /
// `Menos de 2.5` / `Sí` de acá. Medido en
// `elEmpateYLosTotalesLiquidan.test.js`. Ahora los tres llaman al mismo
// `etiquetaResultado`, así que la afirmación de arriba la sostiene el
// código y no la buena voluntad del que copie el próximo botón.
function resultadoCorto(nombre, home, away, corto){
  if(nombre===home) return corto.home;
  if(nombre===away) return corto.away;
  if(nombre==="Draw") return "X";
  if(nombre==="Yes")  return "Sí";
  if(nombre==="No")   return "No";
  let m = nombre.match(/^Over\s*([\d.]+)$/i);
  if(m) return `+${m[1]}`;
  m = nombre.match(/^Under\s*([\d.]+)$/i);
  if(m) return `-${m[1]}`;
  m = nombre.match(/^(.+?)\s*([+-][\d.]+)$/);
  if(m){
    const equipo = m[1]===home?corto.home : m[1]===away?corto.away : m[1];
    return `${equipo} ${m[2]}`;
  }
  return nombre;
}

function CuotasCarrusel({ ev, bets, onToggle, live=false }){
  const [activa,setActiva] = useState(0);
  const pista = useRef(null);
  const gesto = useRef({x:0, scroll:0, arrastro:false});

  const paginas = mercadosDelCarrusel(ev);
  const color = live?Q.pink:Q.violet;
  const home = ev.h||ev.home||"";
  const away = ev.a||ev.away||"";
  const corto = etiquetasDeEquipos(home, away);

  // Sin ningún mercado de fila, la pantalla dice lo mismo que decía antes.
  if(!paginas.length) return(
    <OddsButtons ev={ev} market="h2h" bets={bets} onToggle={onToggle} live={live}/>
  );

  // Un desliz del pulgar corre la pista Y termina en un `click` sobre el
  // botón que quedó debajo del dedo: sin esto, arrastrar para ver el
  // mercado siguiente metía una apuesta en el carrito. Se miran dos
  // cosas, porque ninguna alcanza sola: cuánto se movió el dedo y cuánto
  // se movió la pista. En un desliz táctil el navegador se queda con el
  // gesto y deja de mandar `pointermove`, pero `scrollLeft` igual corrió.
  const alTocar = e => {
    gesto.current = {x:e.clientX, scroll:pista.current?pista.current.scrollLeft:0,
      arrastro:false};
  };
  const alMover = e => {
    if(e.pointerType==="mouse" && e.buttons===0) return;
    if(Math.abs(e.clientX-gesto.current.x)>8) gesto.current.arrastro=true;
  };
  const alClic = e => {
    const corrio = Math.abs((pista.current?pista.current.scrollLeft:0)-gesto.current.scroll)>4;
    if(gesto.current.arrastro||corrio){
      e.preventDefault();
      e.stopPropagation();
      gesto.current.arrastro=false;
    }
  };

  const irA = i => {
    const el = pista.current;
    if(!el) return;
    const quieto = typeof window!=="undefined" && window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollTo({left:i*el.clientWidth, behavior:quieto?"auto":"smooth"});
  };

  return(
    <div>
      <div ref={pista} className="cuotas-carrusel"
        onScroll={e=>{
          const el = e.currentTarget;
          if(!el.clientWidth) return;
          const i = Math.round(el.scrollLeft/el.clientWidth);
          setActiva(a=>a===i?a:i);
        }}
        onPointerDown={alTocar} onPointerMove={alMover} onClickCapture={alClic}
        style={{display:"flex",overflowX:"auto",overflowY:"hidden",
          scrollSnapType:"x mandatory",overscrollBehaviorX:"contain",
          WebkitOverflowScrolling:"touch",scrollbarWidth:"none",
          msOverflowStyle:"none"}}>
        {paginas.map(k=>(
          <div key={k} style={{flex:"0 0 100%",minWidth:0,scrollSnapAlign:"start"}}>
            {/* En minúscula y como lo dice el diccionario de mercados
                ("Más / Menos goles"), no en VERSALITA espaciada: el
                rótulo tiene que leerse de corrido mientras el dedo
                todavía se está moviendo. */}
            <div style={{color:Q.muted,fontSize:12,fontFamily:F_BODY,marginBottom:4,
              overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
              {tituloMercado(k)}
            </div>
            <div style={{display:"flex",gap:SPACING[4]}}>
              {Object.entries(ev.markets[k]||{}).map(([nombre,cuota])=>{
                const etiqueta = etiquetaResultado(nombre, home, away);
                const sel = bets.some(b=>b.id===ev.id && b.label===etiqueta);
                return(
                  // `nombre` es la clave cruda del diccionario del emisor
                  // y viaja QUINTO, al lado de la etiqueta y sin tocarla:
                  // la etiqueta es la identidad del pick en el carrito
                  // (`b.label===etiqueta`, arriba) y el nombre es lo único
                  // que `resolver_una` encuentra en el índice de GR8.
                  <button key={nombre} title={etiqueta}
                    onClick={()=>onToggle(ev, etiqueta, cuota, k, nombre)}
                    style={{flex:"1 1 0",minWidth:0,overflow:"hidden",
                      background:sel?color:Q.inset,border:"none",
                      borderRadius:RADII.md,padding:"8px 4px",cursor:"pointer",
                      textAlign:"center",transition:"background 0.13s"}}>
                    <div style={{color:sel?inkOn(color):Q.dim,fontSize:12,
                      fontFamily:F_BODY,fontWeight:600,letterSpacing:0.6,marginBottom:1,
                      lineHeight:1.1,overflow:"hidden",textOverflow:"ellipsis",
                      whiteSpace:"nowrap",maxWidth:"100%"}}>
                      {resultadoCorto(nombre, home, away, corto)}
                    </div>
                    <div style={{color:sel?inkOn(color):Q.gold,fontWeight:700,
                      fontSize:17,lineHeight:1.05,fontFamily:F_NUM}}>{fmt(cuota)}</div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Los puntitos son además el atajo: el que no quiere deslizar
          cinco veces toca el último. El punto activo se estira en vez de
          solo cambiar de color, que es el único movimiento de esta
          pantalla y contesta al gesto que lo provocó. */}
      {paginas.length>1&&(
        <div style={{display:"flex",justifyContent:"center",gap:SPACING[4],marginTop:6}}>
          {paginas.map((k,i)=>(
            <button key={k} onClick={()=>irA(i)} aria-label={tituloMercado(k)}
              aria-current={i===activa?"true":undefined}
              style={{minHeight:0,width:20,height:20,padding:0,background:"transparent",
                border:"none",cursor:"pointer",display:"grid",placeItems:"center"}}>
              <span className="cuotas-carrusel-punto" style={{
                width:i===activa?14:6,height:6,borderRadius:RADII.full,
                background:i===activa?color:Q.dim,
                transition:"width 0.18s ease, background 0.18s ease"}}/>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── MERCADOS: el diccionario se mudó ─────────────────────────
// `MKT_ES` y los traductores viven en `etiquetasDeMercado.js`, con los
// tres idiomas. Se importan arriba con el nombre de antes.

// ── Que el carrito no tape la fila que acabás de tocar ────────
// El carrito es un panel fijo abajo, así que no ocupa lugar en la lista:
// aparece ENCIMA. Y aparece justo cuando el jugador toca una cuota, o
// sea encima de la fila del partido que acaba de tocar —el mismo botón
// que apretó queda debajo del cartel— que es donde va a querer mirar si
// eligió bien, o cambiar de selección.
//
// Reservarle el alto al final de la lista (lo hace cada pantalla con
// `onAlto`) arregla la mitad: que una fila del final SE PUEDA subir por
// encima del carrito. La otra mitad es esta, porque reservar alto no
// mueve la pantalla sola: al tocar una cuota, se corre lo justo para
// dejar esa fila arriba del cartel, y solo si quedó tapada. Correr la
// pantalla cuando no hace falta es peor que no hacer nada.
//
// El alto del carrito se mide del DOM en vez de pasarse por props: el
// botón de cuota está tres componentes abajo de la pantalla que lo sabe,
// y encadenarle el dato a OddsButtons, MercadosEvento y AllMarketsView
// sería mucho cable para un número que el navegador ya tiene.
function contenedorQueDesplaza(nodo){
  let p = nodo.parentElement;
  while(p){
    const estilo = window.getComputedStyle(p);
    if(/(auto|scroll)/.test(estilo.overflowY) && p.scrollHeight>p.clientHeight) return p;
    p = p.parentElement;
  }
  return null;
}

function despejarCarrito(nodo){
  if(!nodo||typeof nodo.getBoundingClientRect!=="function") return;
  if(typeof requestAnimationFrame!=="function") return;
  // Después del repintado: el carrito recién aparece con esta selección,
  // y antes de eso no tiene alto que medir.
  requestAnimationFrame(()=>{
    try{
      const carrito = document.querySelector("[data-carrito]");
      const alto = carrito ? carrito.getBoundingClientRect().height : 0;
      if(!alto) return;
      const cont = contenedorQueDesplaza(nodo);
      const piso = cont ? cont.getBoundingClientRect().bottom : window.innerHeight;
      const limite = piso - alto - SPACING[8];
      const sobra = nodo.getBoundingClientRect().bottom - limite;
      if(sobra<=0) return;
      const destino = cont || window;
      if(typeof destino.scrollBy==="function"){
        destino.scrollBy({top:sobra,behavior:"smooth"});
      }
    }catch(e){}
  });
}

// La pista se corre con el dedo, y esas reglas no se pueden escribir con
// estilos en línea: son un pseudo-elemento y una media query. Viven en
// una constante y no copiadas en cada `<style>` porque las dibujan dos
// pantallas distintas —el jugador y la agencia— y una regla escrita dos
// veces es una regla que mañana solo se arregla en una.
const CSS_CUOTAS_CARRUSEL = `
/* La pista de cuotas se corre con el dedo y lo dicen los
   puntitos: una barra debajo de cada partido sería la misma
   información dibujada una segunda vez, y en una lista de 199
   tarjetas eso es 199 barras. La regla de arriba fija el ancho
   (barra vertical); a una barra horizontal la mide el alto, así
   que hace falta decirlo aparte. */
.cuotas-carrusel::-webkit-scrollbar{display:none;width:0;height:0}
@media (prefers-reduced-motion: reduce){
  .cuotas-carrusel{scroll-behavior:auto}
  .cuotas-carrusel-punto{transition:none}
}
`;


export {
  fmt,
  partirEquipo,
  etiquetasDeEquipos,
  OddsButtons,
  mercadosDelCarrusel,
  resultadoCorto,
  CuotasCarrusel,
  tituloMercado,
  ordenMercado,
  etiquetaResultado,
  contenedorQueDesplaza,
  despejarCarrito,
  CSS_CUOTAS_CARRUSEL,
};
